import { Component, ElementRef, Inject, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { fabric } from 'fabric';

export interface CollageEditorData {
  photoUrls: string[];
  sectionLabel: string;
  /** Content width the printed report can use for this gallery (matches the backend's COLLAGE_CONTAINER_WIDTH). */
  targetWidthPx?: number;
  /** Starting height guess; the user drags/enters the real value based on space left on the current page. */
  defaultHeightPx?: number;
}

export interface CollageEditorResult {
  dataUrl: string;
  widthPx: number;
  /** Height of the EXPORTED image, which is the canvas height trimmed down to whatever the
   *  tiles actually cover (see usedContentHeight) — not necessarily the height the user set.
   *  A collage that ends early therefore reports a height below COLLAGE_FULL_PAGE_HEIGHT_PX
   *  and is treated as an ordinary gallery rather than a full-page one. */
  heightPx: number;
}

type TemplateFn = (canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number) => void;

/** Matches the backend's cover-page/contact-page full-page convention (245mm content
 *  height in `narrative-impact-report.ts`), converted to CSS px at 96dpi. Offered as a
 *  one-click size preset so a gallery can deliberately take a whole report page. */
export const COLLAGE_FULL_PAGE_HEIGHT_PX = 926;

/** Dialog sizing for the editor: a fixed-height panel whose middle scrolls, so the header and
 *  action buttons stay on screen however tall the collage is. Spread into dialog.open(). */
export const COLLAGE_EDITOR_DIALOG_CONFIG: MatDialogConfig = {
  width: '1120px',
  maxWidth: '96vw',
  height: '92vh',
  panelClass: 'collage-editor-dialog',
  autoFocus: 'dialog', // not the Width field — typing a stray key there would resize the collage
};

/**
 * Interactive collage builder used by the Narrative Impact Report wizard.
 * Renders candidate layouts on a Fabric.js canvas so the user can pick one
 * ("Rearrange" cycles templates) and size it to whatever space is left on
 * the current report page, then exports a single flattened image. The
 * generated Cloud Function HTML never depends on Fabric — it just receives
 * the exported data URL like any other uploaded photo.
 */
@Component({
  selector: 'app-collage-editor',
  templateUrl: './collage-editor.component.html',
  styleUrls: ['./collage-editor.component.scss'],
})
export class CollageEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('collageCanvas', { static: true }) canvasEl!: ElementRef<HTMLCanvasElement>;

  canvas!: fabric.Canvas;
  widthPx: number;
  heightPx: number;
  // Starts true: ngAfterViewInit immediately begins loading the photos, and flipping this from
  // false to true inside that hook changed [disabled]/*ngIf bindings Angular had just checked
  // (ExpressionChangedAfterItHasBeenCheckedError in dev mode).
  generating = true;
  /** Height the collage will actually export at (tiles' real extent — see usedContentHeight). */
  exportHeightPx = 0;
  templateIndex = 0;
  templateName = '';

  private images: fabric.Image[] = [];
  private readonly templates: { name: string; minPhotos: number; maxPhotos: number; fn: TemplateFn }[] = [
    // Listed first so it's the design shown by default: adaptive, justified-row packing
    // sized from each photo's own aspect ratio, so nothing is ever cropped.
    { name: 'Uncropped Adaptive Grid', minPhotos: 1, maxPhotos: 24, fn: this.layoutUncroppedAdaptiveGrid.bind(this) },
    { name: 'Filmstrip', minPhotos: 1, maxPhotos: 4, fn: this.layoutFilmstrip.bind(this) },
    { name: 'Vertical Strip', minPhotos: 1, maxPhotos: 4, fn: this.layoutVerticalStrip.bind(this) },
    { name: 'Mosaic', minPhotos: 3, maxPhotos: 6, fn: this.layoutMosaic.bind(this) },
    { name: 'Big Feature', minPhotos: 3, maxPhotos: 8, fn: this.layoutBigFeature.bind(this) },
    { name: 'Grid', minPhotos: 4, maxPhotos: 12, fn: this.layoutGrid.bind(this) },
    { name: 'Split Rows', minPhotos: 4, maxPhotos: 12, fn: this.layoutSplitRows.bind(this) },
    { name: 'Scrapbook', minPhotos: 3, maxPhotos: 10, fn: this.layoutScrapbook.bind(this) },
  ];

  readonly fullPageHeightPx = COLLAGE_FULL_PAGE_HEIGHT_PX;

  /** One-click preset: size the collage to occupy a whole report page. The report
   *  component checks the exported height against this constant to decide whether
   *  to flag the gallery `*CollageFullPage`, so the backend gives it its own page. */
  useFullPageHeight(): void {
    this.heightPx = this.fullPageHeightPx;
    this.applySize();
  }

  constructor(
    private dialogRef: MatDialogRef<CollageEditorComponent, CollageEditorResult>,
    @Inject(MAT_DIALOG_DATA) public data: CollageEditorData,
  ) {
    this.widthPx = data.targetWidthPx || 672;
    // 700 (was 260) so a collage starts out using most of a report page rather than a thin
    // strip. Safe to start large now that layoutUncroppedAdaptiveGrid clamps its upscale to 1
    // and useCollage() trims the export to the tiles' real extent — a few photos no longer get
    // blown up to fill this height, they just export shorter and let the report's
    // placeholder-fill top the page up.
    this.heightPx = data.defaultHeightPx || 700;
  }

  async ngAfterViewInit(): Promise<void> {
    this.canvas = new fabric.Canvas(this.canvasEl.nativeElement, {
      selection: false,
      preserveObjectStacking: true,
      // JPEG has no alpha channel — without an explicit background, toDataURL()
      // flattens any transparent gap between tiles (or letterboxed edge) to black.
      backgroundColor: '#ffffff',
    });
    this.canvas.setDimensions({ width: this.widthPx, height: this.heightPx });
    // Clamps a pannable tile's drag to panBounds (set in makePannable below) so the
    // enlarged image can never be dragged far enough to expose empty space at an edge
    // of its fixed crop window.
    this.canvas.on('object:moving', (e: fabric.IEvent) => {
      const obj = e.target as (fabric.Object & { panBounds?: { minLeft: number; maxLeft: number; minTop: number; maxTop: number } }) | undefined;
      const bounds = obj?.panBounds;
      if (!obj || !bounds) return;
      obj.left = Math.min(bounds.maxLeft, Math.max(bounds.minLeft, obj.left ?? 0));
      obj.top = Math.min(bounds.maxTop, Math.max(bounds.minTop, obj.top ?? 0));
    });
    await this.loadImages();
    this.applicableTemplates().length && this.renderTemplate(0);
  }

  ngOnDestroy(): void {
    this.canvas?.dispose();
  }

  private async loadImages(): Promise<void> {
    this.generating = true;
    const loaded = await Promise.all(
      this.data.photoUrls.map(
        url =>
          new Promise<fabric.Image>(resolve => {
            fabric.Image.fromURL(
              url,
              (img: fabric.Image) => resolve(img),
              { crossOrigin: 'anonymous' },
            );
          }),
      ),
    );
    this.images = loaded;
    this.generating = false;
  }

  /** Only offer templates whose photo-count range fits the current gallery. */
  private applicableTemplates() {
    const n = this.images.length;
    return this.templates.filter(t => n >= t.minPhotos && n <= t.maxPhotos);
  }

  private renderTemplate(index: number): void {
    const applicable = this.applicableTemplates();
    if (!applicable.length) return;
    const t = applicable[index % applicable.length];
    this.templateName = t.name;
    this.canvas.clear();
    // fabric.Canvas#clear() resets backgroundColor to '' — restore it every render,
    // not just once in the constructor, or a post-Rearrange export goes back to
    // flattening transparent gaps to black on JPEG export.
    this.canvas.backgroundColor = '#ffffff';
    t.fn(this.canvas, this.images, this.widthPx, this.heightPx);
    this.canvas.renderAll();
    this.exportHeightPx = this.usedContentHeight();
  }

  /** "Rearrange" — try the next template design; falls back to reshuffling
   *  photo order within the same template once every design has been shown. */
  rearrange(): void {
    const applicable = this.applicableTemplates();
    if (!applicable.length) return;
    this.templateIndex++;
    if (this.templateIndex % applicable.length === 0) {
      this.images = this.shuffle([...this.images]);
    }
    this.renderTemplate(this.templateIndex);
  }

  /** Re-renders the current template at the (possibly just-edited) canvas size. */
  applySize(): void {
    this.canvas.setDimensions({ width: this.widthPx, height: this.heightPx });
    this.renderTemplate(this.templateIndex);
  }

  /** Thumbnail source list for the reorder strip, in the same order layout templates
   *  will consume — i.e. dragging thumbnail #1 to the end really does make it the last
   *  photo placed (the "hero" slot in Mosaic/Big Feature is always images[0]). */
  get thumbnailUrls(): string[] {
    return this.images.map(img => img.getSrc());
  }

  /** Drag-to-reorder the source photo order (matches the cdkDropList pattern already
   *  used elsewhere in this app, e.g. revised-child-report.component.ts). Re-renders
   *  immediately so the effect on the current template is visible right away. */
  reorderPhotos(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.images, event.previousIndex, event.currentIndex);
    this.renderTemplate(this.templateIndex);
  }

  /** Height the rendered tiles actually cover, so a template that ends early (the adaptive
   *  grid with only a few photos, whose upscale is now clamped to 1) doesn't export a tall
   *  band of blank canvas background. Measured from the objects themselves rather than
   *  reported per-template, so it stays right for every layout: the placeCover templates
   *  deliberately overflow their tiles, so their bounding boxes always reach (or exceed)
   *  the canvas height and the clamp below leaves them exporting full height as before. */
  private usedContentHeight(): number {
    const objects = this.canvas.getObjects();
    if (!objects.length) return this.heightPx;
    let maxBottom = 0;
    objects.forEach(obj => {
      const rect = obj.getBoundingRect(true);
      maxBottom = Math.max(maxBottom, rect.top + rect.height);
    });
    return Math.max(1, Math.min(this.heightPx, Math.ceil(maxBottom)));
  }

  useCollage(): void {
    // Selection borders/handles live on Fabric's separate overlay canvas and are never
    // part of toDataURL()'s output — discarding first is just defensive hygiene, not a
    // correctness fix, so a mid-drag selection can't leave any visual trace.
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
    // Crop the export to the height the tiles really occupy. heightPx is what the report
    // component tests against COLLAGE_FULL_PAGE_HEIGHT_PX, so a collage that ends early
    // correctly stops claiming a whole page and instead flows as an ordinary gallery — which
    // the report's placeholder-fill then tops up to a full page.
    const exportHeight = this.usedContentHeight();
    const dataUrl = this.canvas.toDataURL({
      format: 'jpeg',
      quality: 0.92,
      left: 0,
      top: 0,
      width: this.widthPx,
      height: exportHeight,
    });
    this.dialogRef.close({ dataUrl, widthPx: this.widthPx, heightPx: exportHeight });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // ---- Layout templates -------------------------------------------------
  // Each template positions clones of the loaded fabric.Image objects onto
  // the canvas at (w, h). Cropping uses cover-style clipPath so faces stay
  // roughly centred, matching the framing convention used by the backend's
  // justified-row collage (object-position: center top).

  /** Lets a cropped tile be dragged to reveal a different part of the source photo
   *  through its crop window — e.g. to recover a face the automatic "cover" crop cut
   *  off. The window itself doesn't move: `clipPath` on the image is `absolutePositioned`,
   *  so it stays pinned at the tile's fixed canvas location regardless of how the
   *  (enlarged) image beneath it is dragged. `panBounds` — read by the `object:moving`
   *  handler wired up in ngAfterViewInit — clamps that drag so the image can never be
   *  pulled far enough to expose empty space at an edge. Re-running a template
   *  (Rearrange / resize) clears and rebuilds every tile from scratch, so a manual pan
   *  resets along with everything else. */
  private makePannable(obj: fabric.Image, tileX: number, tileY: number, tileW: number, tileH: number, imgW: number, imgH: number): fabric.Image {
    obj.set({
      selectable: true,
      hasControls: false,
      hasBorders: false, // the object's real (unclipped) bounds are bigger than the tile — a border would be misleading
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
      hoverCursor: 'move',
    });
    (obj as fabric.Object & { panBounds?: unknown }).panBounds = {
      minLeft: tileX + tileW - imgW / 2,
      maxLeft: tileX + imgW / 2,
      minTop: tileY + tileH - imgH / 2,
      maxTop: tileY + imgH / 2,
    };
    return obj;
  }

  private placeCover(img: fabric.Image, x: number, y: number, w: number, h: number, rotationDeg = 0): fabric.Image {
    const clone = fabric.util.object.clone(img) as fabric.Image;
    const srcW = clone.width || 1;
    const srcH = clone.height || 1;
    const scale = Math.max(w / srcW, h / srcH);
    clone.set({
      left: x + w / 2,
      top: y + h / 2,
      originX: 'center',
      originY: 'center',
      scaleX: scale,
      scaleY: scale,
      angle: rotationDeg,
      clipPath: new fabric.Rect({
        left: x + w / 2,
        top: y + h / 2,
        width: w,
        height: h,
        originX: 'center',
        originY: 'center',
        absolutePositioned: true,
      }),
    });
    // Panning maths assumes an axis-aligned crop window — skip it for rotated tiles
    // (Scrapbook) rather than clamp incorrectly against a rotated bounding box.
    if (rotationDeg === 0) {
      this.makePannable(clone, x, y, w, h, srcW * scale, srcH * scale);
    } else {
      clone.set({ selectable: false });
    }
    this.canvas.add(clone);
    return clone;
  }

  /** Places an image scaled to *fit inside* w×h (no clipPath, no cropping) — the
   *  inverse of placeCover. Used only by layouts that already size each tile to the
   *  image's own aspect ratio, so the whole photo is always visible already; there's
   *  nothing to pan. */
  private placeContain(img: fabric.Image, x: number, y: number, w: number, h: number): fabric.Image {
    const clone = fabric.util.object.clone(img) as fabric.Image;
    const srcW = clone.width || 1;
    const srcH = clone.height || 1;
    const scale = Math.min(w / srcW, h / srcH);
    clone.set({
      left: x + w / 2,
      top: y + h / 2,
      originX: 'center',
      originY: 'center',
      scaleX: scale,
      scaleY: scale,
      selectable: false,
    });
    this.canvas.add(clone);
    return clone;
  }

  /** "Uncropped Adaptive Grid" — ports the same justified-row DP packing the backend
   *  uses for its automatic photo grid (`packJustifiedRows` in narrative-impact-report.ts):
   *  row membership is chosen together (not greedily) to minimize deviation from a target
   *  row height, so no lone leftover photo gets stretched into a shape that would need
   *  cropping. Row height is then solved exactly from each row's own summed aspect ratios
   *  (no cropping needed by construction), and the whole stacked block is scaled uniformly
   *  to fit the canvas height the user set, letterboxing any leftover row width instead of
   *  stretching or cropping a single image. */
  private layoutUncroppedAdaptiveGrid(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const gap = 8;
    const targetRowHeight = 170;
    const maxPerRow = 6;
    const ratios = images.map(img => (img.width || 1) / (img.height || 1));
    const n = images.length;

    const prefixRatio: number[] = [0];
    for (const r of ratios) prefixRatio.push(prefixRatio[prefixRatio.length - 1] + r);

    const rowHeightFor = (start: number, end: number): number => {
      const sumRatio = prefixRatio[end] - prefixRatio[start];
      const gapsWidth = gap * (end - start - 1);
      return (w - gapsWidth) / sumRatio;
    };
    const rowCost = (start: number, end: number): number => {
      const rh = rowHeightFor(start, end);
      if (!isFinite(rh) || rh <= 0) return Infinity;
      const dev = (rh - targetRowHeight) / targetRowHeight;
      return dev * dev;
    };

    const dpCost: number[] = new Array(n + 1).fill(Infinity);
    const dpBreak: number[] = new Array(n + 1).fill(0);
    dpCost[0] = 0;
    for (let end = 1; end <= n; end++) {
      const earliest = Math.max(0, end - maxPerRow);
      for (let start = earliest; start < end; start++) {
        if (dpCost[start] === Infinity) continue;
        const cost = dpCost[start] + rowCost(start, end);
        if (cost < dpCost[end]) {
          dpCost[end] = cost;
          dpBreak[end] = start;
        }
      }
    }

    const breakpoints: number[] = [n];
    for (let cursor = n; cursor > 0; cursor = dpBreak[cursor]) breakpoints.push(dpBreak[cursor]);
    breakpoints.reverse();

    const rows: { start: number; end: number; height: number }[] = [];
    for (let k = 0; k < breakpoints.length - 1; k++) {
      const start = breakpoints[k];
      const end = breakpoints[k + 1];
      rows.push({ start, end, height: Math.max(1, rowHeightFor(start, end)) });
    }

    const naturalTotalHeight = rows.reduce((sum, row) => sum + row.height, 0) + gap * (rows.length - 1);
    // Clamped to 1 in the upward direction. Each row's height was solved so that row fills
    // exactly the canvas width, so ANY upscale widens every row past the canvas edge: with
    // 3-4 photos on a "Full Page Height" canvas that was a 4-6x blow-up, producing rows
    // thousands of px wide that got clipped to a narrow centre slice of each photo. Scaling
    // *down* (many photos, block taller than the canvas) is still correct and left alone.
    // When the block now ends early, useCollage() trims the unused canvas off the export so
    // the report fills that space with a placeholder instead of baking in white background.
    const scale = naturalTotalHeight > 0 ? Math.min(1, h / naturalTotalHeight) : 1;

    let y = 0;
    rows.forEach(row => {
      const rowHeight = row.height * scale;
      const rowImages = images.slice(row.start, row.end);
      const rowRatios = ratios.slice(row.start, row.end);
      const rowWidth = rowRatios.reduce((sum, r) => sum + r * rowHeight, 0) + gap * (rowImages.length - 1);
      let x = (w - rowWidth) / 2;
      rowImages.forEach((img, i) => {
        const tileW = rowRatios[i] * rowHeight;
        this.placeContain(img, x, y, tileW, rowHeight);
        x += tileW + gap;
      });
      y += rowHeight + gap;
    });
  }

  private layoutFilmstrip(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const n = images.length;
    const gap = 6;
    const tileW = (w - gap * (n - 1)) / n;
    images.forEach((img, i) => this.placeCover(img, i * (tileW + gap), 0, tileW, h));
  }

  /** Filmstrip stacked vertically instead of side-by-side — a better fit than the
   *  horizontal Filmstrip when the canvas is tall relative to its width (e.g. a
   *  "Full Page Height" collage), since a horizontal strip stretched that tall would
   *  leave each tile absurdly narrow. */
  private layoutVerticalStrip(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const n = images.length;
    const gap = 6;
    const tileH = (h - gap * (n - 1)) / n;
    images.forEach((img, i) => this.placeCover(img, 0, i * (tileH + gap), w, tileH));
  }

  /** Magazine-cover style: one hero photo spans the full width across the top, the
   *  rest fill a grid below it. Distinct rhythm from Mosaic, which splits left/right
   *  instead of top/bottom. */
  private layoutBigFeature(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const gap = 6;
    const mainH = h * 0.6;
    this.placeCover(images[0], 0, 0, w, mainH - gap / 2);
    const rest = images.slice(1);
    const cols = Math.min(rest.length, 4);
    const rows = Math.ceil(rest.length / cols);
    const tileW = (w - gap * (cols - 1)) / cols;
    const tileH = (h - mainH - gap / 2 - gap * (rows - 1)) / rows;
    rest.forEach((img, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      this.placeCover(img, col * (tileW + gap), mainH + gap / 2 + row * (tileH + gap), tileW, tileH);
    });
  }

  /** Exactly two even horizontal rows (photos split as evenly as possible between
   *  them), each row's tiles equal width — a cleaner, more symmetric look than Grid's
   *  fixed 2-or-3-column packing for the same photo count. */
  private layoutSplitRows(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const gap = 6;
    const topCount = Math.ceil(images.length / 2);
    const topRow = images.slice(0, topCount);
    const bottomRow = images.slice(topCount);
    const rowH = (h - gap) / 2;
    const placeRow = (row: fabric.Image[], y: number) => {
      const tileW = (w - gap * (row.length - 1)) / row.length;
      row.forEach((img, i) => this.placeCover(img, i * (tileW + gap), y, tileW, rowH));
    };
    placeRow(topRow, 0);
    placeRow(bottomRow, rowH + gap);
  }

  private layoutMosaic(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const gap = 6;
    const mainW = w * 0.6;
    this.placeCover(images[0], 0, 0, mainW - gap / 2, h);
    const rest = images.slice(1);
    const rowH = (h - gap * (rest.length - 1)) / rest.length;
    rest.forEach((img, i) => this.placeCover(img, mainW + gap / 2, i * (rowH + gap), w - mainW - gap / 2, rowH));
  }

  private layoutGrid(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const gap = 6;
    const cols = images.length <= 4 ? 2 : 3;
    const rows = Math.ceil(images.length / cols);
    const tileW = (w - gap * (cols - 1)) / cols;
    const tileH = (h - gap * (rows - 1)) / rows;
    images.forEach((img, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      this.placeCover(img, col * (tileW + gap), row * (tileH + gap), tileW, tileH);
    });
  }

  private layoutScrapbook(canvas: fabric.Canvas, images: fabric.Image[], w: number, h: number): void {
    const cols = 3;
    const rows = Math.ceil(images.length / cols);
    const tileW = w / cols;
    const tileH = h / rows;
    const border = 8;
    images.forEach((img, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const rotation = Math.random() * 10 - 5;
      const x = col * tileW + border;
      const y = row * tileH + border;
      const tile = this.placeCover(img, x, y, tileW - border * 2, tileH - border * 2 - 10, rotation);
      tile.set({ shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.25)', blur: 6, offsetX: 2, offsetY: 2 }) });
    });
  }
}
